import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Brain, Battery, Moon } from 'lucide-react';
import { HealthMetrics } from '../../types/health';

interface RecoveryBreakdownProps {
  metrics: HealthMetrics;
}

export function RecoveryBreakdown({ metrics }: RecoveryBreakdownProps) {
  const components = [
    {
      name: 'Heart Rate Variability',
      score: 85,
      icon: <Heart className="w-5 h-5 text-primary" />,
      details: [
        { label: 'Current', value: `${metrics.hrv.current}ms` },
        { label: 'Baseline', value: `${metrics.hrv.yesterday}ms` },
        { label: 'Trend', value: 'Improving' }
      ],
      color: 'from-primary/20 to-purple-500/20'
    },
    {
      name: 'Sleep Quality',
      score: metrics.sleep.score,
      icon: <Moon className="w-5 h-5 text-purple-500" />,
      details: [
        { label: 'Duration', value: `${metrics.sleep.hours}h` },
        { label: 'Efficiency', value: '92%' },
        { label: 'Deep Sleep', value: '1.6h' }
      ],
      color: 'from-purple-500/20 to-blue-500/20'
    },
    {
      name: 'Nervous System',
      score: 78,
      icon: <Brain className="w-5 h-5 text-blue-500" />,
      details: [
        { label: 'Stress', value: `${metrics.stress.current}%` },
        { label: 'Balance', value: 'Optimal' },
        { label: 'Readiness', value: `${metrics.readiness}%` }
      ],
      color: 'from-blue-500/20 to-cyan-500/20'
    }
  ];

  return (
    <div className="theme-card p-6">
      <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
        <Battery className="w-5 h-5 text-primary" />
        Recovery Components
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {components.map((component, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="relative overflow-hidden rounded-lg p-4"
          >
            {/* Background Gradient */}
            <div className={`absolute inset-0 bg-gradient-to-br ${component.color}`} />
            
            {/* Content */}
            <div className="relative space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {component.icon}
                  <span className="font-medium">{component.name}</span>
                </div>
                <div className="text-2xl font-bold">{component.score}%</div>
              </div>

              <div className="space-y-2">
                {component.details.map((detail, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{detail.label}</span>
                    <span className="font-medium">{detail.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}